'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';

interface EmergencyButtonProps {
  variant?: 'header' | 'overlay'; // header = in blue header bar, overlay = on dark screens
}

export default function EmergencyButton({ variant = 'header' }: EmergencyButtonProps) {
  const router = useRouter();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showNoPhone, setShowNoPhone] = useState(false);

  const handleEmergencyClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmCall = () => {
    setShowConfirm(false);
    // Simulate: in a real Android app, this would open the phone dialer with 18
    // For prototype demo, show the demo screen
    setShowDemo(true);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setShowDemo(false);
    setShowNoPhone(false);
  };

  const handleBackToApp = () => {
    setShowDemo(false);
    setShowNoPhone(false);
  };

  const handleNotifyFamily = () => {
    setShowNoPhone(false);
    router.push('/contact-familial');
  };

  const buttonStyle: React.CSSProperties = variant === 'header' ? {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#B91C1C',
    border: '2px solid #991B1B',
    borderRadius: '12px',
    padding: '0 20px',
    height: '64px',
    minWidth: '200px',
    cursor: 'pointer',
    color: 'white',
    fontSize: '20px',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    boxShadow: '0 3px 12px rgba(185,28,28,0.5)',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  } : {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#B91C1C',
    border: '2px solid #991B1B',
    borderRadius: '12px',
    padding: '0 20px',
    height: '64px',
    minWidth: '200px',
    cursor: 'pointer',
    color: 'white',
    fontSize: '20px',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    boxShadow: '0 3px 16px rgba(185,28,28,0.6)',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  };

  return (
    <>
      <button
        onClick={handleEmergencyClick}
        aria-label="Urgence, appeler les pompiers au 18"
        style={buttonStyle}
      >
        {/* Phone icon */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <span>Urgence – Pompiers 18</span>
      </button>

      {/* ── CONFIRMATION MODAL ── */}
      {showConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10,22,40,0.88)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Confirmation appel d'urgence"
        >
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '48px 56px',
            maxWidth: '720px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            textAlign: 'center',
          }}>
            {/* Warning icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#FEE2E2',
              border: '3px solid #B91C1C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>

            {/* Title */}
            <div style={{ fontSize: '36px', fontWeight: 800, color: '#1A2744', lineHeight: 1.2 }}>
              Appeler les pompiers ?
            </div>

            {/* Explanation */}
            <div style={{ fontSize: '22px', fontWeight: 500, color: '#374151', lineHeight: 1.5 }}>
              Le 18 est réservé aux situations urgentes : personne en danger, incendie, accident ou malaise grave.
            </div>

            {/* Question */}
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#1A2744', lineHeight: 1.4 }}>
              Souhaitez-vous appeler les pompiers maintenant ?
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
              <button
                onClick={handleConfirmCall}
                aria-label="Confirmer l'appel aux pompiers au 18"
                style={{
                  minHeight: '88px',
                  background: '#B91C1C',
                  border: '3px solid #991B1B',
                  borderRadius: '16px',
                  color: 'white',
                  fontSize: '26px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '14px',
                  boxShadow: '0 4px 20px rgba(185,28,28,0.4)',
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Appeler les pompiers – 18
              </button>

              <button
                onClick={handleCancel}
                aria-label="Annuler et revenir"
                style={{
                  minHeight: '88px',
                  background: '#F3F4F6',
                  border: '2.5px solid #D1D5DB',
                  borderRadius: '16px',
                  color: '#374151',
                  fontSize: '26px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                }}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Non, revenir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DEMO SCREEN — Ouverture du téléphone ── */}
      {showDemo && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10,22,40,0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Simulation ouverture téléphone"
        >
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '48px 56px',
            maxWidth: '680px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            textAlign: 'center',
          }}>
            {/* Prototype badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '20px',
              padding: '8px 20px',
              margin: '0 auto',
              fontSize: '18px',
              fontWeight: 700,
              color: '#92400E',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Mode démonstration
            </div>

            {/* Phone icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#DCFCE7',
              border: '3px solid #16A34A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>

            <div style={{ fontSize: '32px', fontWeight: 800, color: '#1A2744' }}>
              Ouverture du téléphone
            </div>

            <div style={{ fontSize: '22px', fontWeight: 500, color: '#374151', lineHeight: 1.6 }}>
              Sur la tablette Android, le téléphone s'ouvrira avec le numéro <strong>18</strong>.
            </div>

            <div style={{
              background: '#F0FBE8',
              border: '2px solid rgba(93,187,31,0.4)',
              borderRadius: '14px',
              padding: '16px 20px',
              fontSize: '18px',
              color: '#374151',
              lineHeight: 1.5,
            }}>
              <strong>Note :</strong> Aucun appel réel n'est lancé pendant les tests du prototype. L'appel d'urgence sera géré par le système téléphonique Android natif.
            </div>

            <button
              onClick={handleBackToApp}
              aria-label="Revenir à l'application"
              style={{
                minHeight: '80px',
                background: 'linear-gradient(135deg, #1A2744 0%, #2D3F6B 100%)',
                border: 'none',
                borderRadius: '16px',
                color: 'white',
                fontSize: '24px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '8px',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Revenir à l'application
            </button>
          </div>
        </div>
      )}

      {/* ── NO PHONE CAPABILITY SCREEN ── */}
      {showNoPhone && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10,22,40,0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
          }}
          role="dialog"
          aria-modal="true"
        >
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '48px 56px',
            maxWidth: '680px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '32px', fontWeight: 800, color: '#1A2744' }}>
              Appel impossible
            </div>
            <div style={{ fontSize: '22px', fontWeight: 500, color: '#374151', lineHeight: 1.6 }}>
              Cette tablette ne peut pas passer d'appel téléphonique. Utilisez un téléphone pour composer le <strong>18</strong> ou le <strong>112</strong>.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
              <button
                onClick={handleBackToApp}
                aria-label="Revenir à l'accueil"
                style={{
                  minHeight: '80px',
                  background: 'linear-gradient(135deg, #1A2744 0%, #2D3F6B 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  color: 'white',
                  fontSize: '24px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                }}
              >
                Revenir à l'accueil
              </button>
              <button
                onClick={handleNotifyFamily}
                aria-label="Prévenir ma famille"
                style={{
                  minHeight: '80px',
                  background: '#F3F4F6',
                  border: '2.5px solid #D1D5DB',
                  borderRadius: '16px',
                  color: '#374151',
                  fontSize: '24px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                }}
              >
                Prévenir ma famille
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
